import { AxiosRequestConfig } from 'axios';

/**
 * Counts the student's changes on their way to a server: any request other than a read, from when it is sent until it
 * answers. Cmd+Z and the calendar's Undo button wait for these (undoLatest), so the change just made is in Undo's
 * history before they take the newest one: a drop pressed Cmd+Z on a moment before its save answered would otherwise
 * undo the change before it.
 */

// Requests that write without the student changing anything: a token refresh, the onboarding badges and the trial day
// they earn, the term's setup step moving on after its first task, an Auto-plan preview.
const isNotAChange = (url: string, body: unknown) =>
  /\/refresh-token$/.test(url) ||
  /\/badges$/.test(url) ||
  /\/membership\/add-trial-day$/.test(url) ||
  /\/change-state$/.test(url) ||
  // Activity log reports go on their own instance anyway; never a change of the student's.
  /\/me\/actions$/.test(url) ||
  (!!body && typeof body === 'object' && (body as { dryRun?: boolean }).dryRun === true);

// A request as sent, or as its answer carries it (apisauce's own copy of axios types it differently).
type Request = Pick<AxiosRequestConfig, 'url' | 'data'> & { method?: string };

/** Anything other than a read, minus the ones above. Also asked once the request is sent, when its body is a string. */
export const isStudentChange = ({ method, url = '', data }: Request) => {
  if (!method || method.toLowerCase() === 'get') return false;
  let body = data;
  if (typeof data === 'string') {
    try {
      body = JSON.parse(data);
    } catch {}
  }
  return !isNotAChange(url, body);
};

let inFlight = 0;
const onSettled = new Set<() => void>();

/** As a request is sent. */
export const writeSent = (request: Request) => {
  if (isStudentChange(request)) inFlight += 1;
};

/** As it answers, or fails to: the same request as sent, so the same question gives the same answer. */
export const writeAnswered = (request: Request | undefined) => {
  if (!request || !isStudentChange(request) || inFlight === 0) return;
  inFlight -= 1;
  if (inFlight === 0) Array.from(onSettled).forEach(settled => settled());
};

export const writesInFlight = () => inFlight;

/**
 * Resolves once no change is on its way, or after `timeoutMs` at most: a slow server mustn't make Undo hang. A moment
 * after the last one answers, so the code that asked for it has taken its answer (and kept the change for Undo) first.
 */
export const writesSettled = (timeoutMs: number) =>
  new Promise<void>(resolve => {
    if (inFlight === 0) return resolve();
    const settled = () => {
      window.clearTimeout(timer);
      onSettled.delete(settled);
      window.setTimeout(resolve, 0);
    };
    const timer = window.setTimeout(settled, timeoutMs);
    onSettled.add(settled);
  });
